"use client";

import type { CSSProperties, ReactNode } from "react";
import { SidebarInset, SidebarProvider } from "../components/sidebar";
import { AppSidebar, type AppSidebarProps } from "./app-sidebar";

export interface DashboardShellProps extends Omit<AppSidebarProps, "variant"> {
	/** Rendered above the page content inside the inset (usually <SiteHeader />). */
	header?: ReactNode;
	children: ReactNode;
	sidebarVariant?: AppSidebarProps["variant"];
	/** Extra classes on the content wrapper. */
	contentClassName?: string;
}

const SHELL_STYLE = {
	"--sidebar-width": "calc(var(--spacing) * 64)",
	"--header-height": "calc(var(--spacing) * 12)",
} as CSSProperties;

export function DashboardShell({
	header,
	children,
	sidebarVariant = "inset",
	contentClassName = "flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6",
	...sidebarProps
}: DashboardShellProps) {
	return (
		<SidebarProvider style={SHELL_STYLE}>
			<AppSidebar variant={sidebarVariant} {...sidebarProps} />
			{/*
			 * `min-w-0` keeps a wide page from stretching the inset. The inset is a
			 * flex item, so its automatic minimum is its content's minimum: one
			 * table too wide for the viewport pushed the whole shell sideways,
			 * sticky header included, instead of scrolling inside its own
			 * container.
			 */}
			<SidebarInset className="min-w-0">
				{header}
				<div className={contentClassName}>{children}</div>
			</SidebarInset>
		</SidebarProvider>
	);
}
